import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Activity, Mail, ArrowRight, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [resetUrl, setResetUrl] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post(`${API}/auth/forgot-password`, { email });
            setSuccess(true);
            // For testing purposes, show the reset URL
            if (response.data.reset_url) {
                setResetUrl(response.data.reset_url);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 hero-gradient">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center shadow-lg shadow-primary/20">
                            <Activity className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">
                            Medi<span className="text-primary">Guide</span>
                        </span>
                    </Link>
                </div>

                <Card className="border-slate-200 dark:border-slate-700 shadow-xl" data-testid="forgot-password-card">
                    <CardHeader className="space-y-1 text-center pb-2">
                        <CardTitle className="font-heading text-2xl">Reset Password</CardTitle>
                        <CardDescription>
                            Enter your email to receive a password reset link
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {success ? (
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                                    <CheckCircle2 className="w-8 h-8 text-success" />
                                </div>
                                <p className="text-muted-foreground">
                                    If an account exists with this email, you will receive a password reset link.
                                </p>
                                {resetUrl && (
                                    <div className="p-3 bg-muted rounded-lg text-sm">
                                        <p className="text-muted-foreground mb-2">For testing, use this link:</p>
                                        <Button 
                                            variant="link" 
                                            onClick={() => navigate(resetUrl)}
                                            className="text-primary"
                                        >
                                            Reset Password
                                        </Button>
                                    </div>
                                )}
                                <Link to="/login">
                                    <Button variant="outline" className="w-full mt-4">
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Back to Login
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>

                                <Button 
                                    type="submit" 
                                    className="w-full rounded-full" 
                                    disabled={loading}
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <div className="spinner w-5 h-5" />
                                    ) : (
                                        <>
                                            Send Reset Link
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>

                                <Link to="/login" className="block text-center">
                                    <Button variant="ghost" className="text-muted-foreground">
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Back to Login
                                    </Button>
                                </Link>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
